import React, { useState, useEffect, useCallback } from 'react';
import { jobsAPI } from '../../utils/api';
import { fetchAllPages } from '../../utils/fetchAllPages';
import InterviewFeedbackDrawer from './InterviewFeedbackDrawer';

/**
 * The interviewer's queue: what needs them now. Interviews that have ended
 * without feedback come first, then what is coming up. Built from the same
 * endpoint as the schedule view; grouping happens here.
 */
const DAY_MS = 24 * 60 * 60 * 1000;

function endOf(interview) {
  return new Date(new Date(interview.scheduledDate).getTime() + (interview.duration || 60) * 60 * 1000);
}

export function groupInterviews(interviews, now = new Date()) {
  const needsFeedback = [], upcoming = [], done = [];
  for (const i of interviews) {
    if (i.status === 'cancelled') continue;
    const ended = endOf(i) < now;
    const hasFeedback = Boolean((i.feedback && i.feedback.trim()) || i.rating);
    if (ended && !hasFeedback && i.status !== 'no-show') needsFeedback.push(i);
    else if (!ended) upcoming.push(i);
    else done.push(i);
  }
  needsFeedback.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
  upcoming.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
  return { needsFeedback, upcoming, done };
}

function daysAgo(date, now = new Date()) {
  const d = Math.floor((now - new Date(date)) / DAY_MS);
  if (d <= 0) return 'today';
  return d === 1 ? 'yesterday' : `${d} days ago`;
}

function when(date) {
  return new Date(date).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function InterviewQueue() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedbackInterview, setFeedbackInterview] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const all = await fetchAllPages(
        (pageParams) => jobsAPI.getMyInterviews({ ...pageParams }),
        (data) => data.interviews
      );
      setInterviews(all);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load your interviews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const { needsFeedback, upcoming, done } = groupInterviews(interviews);
  const soon = upcoming.filter(i => new Date(i.scheduledDate) - Date.now() < 7 * DAY_MS);

  const row = (i, action) => (
    <div key={i._id} className="assessment-card">
      <div className="card-header">
        <h3>{i.candidate?.name || 'Candidate'}</h3>
        <span className={`badge badge-${i.status}`}>{i.status}</span>
      </div>
      <div className="card-body">
        <div className="info-row"><span className="label">Position:</span><span className="value">{i.job?.title || i.jobPostingId?.title || '-'}</span></div>
        <div className="info-row"><span className="label">When:</span><span className="value">{when(i.scheduledDate)} ({i.duration} min)</span></div>
        {i.assessment?.assigned && (
          <div className="info-row"><span className="label">Assessment:</span><span className="value">{i.assessment.completed ? 'completed' : 'assigned, not completed'}</span></div>
        )}
      </div>
      <div className="card-actions">{action}</div>
    </div>
  );

  return (
    <div className="assessments-section">
      <div className="filter-bar">
        <h2>My Queue</h2>
        <span style={{ color: '#6B7280', fontSize: '0.9rem' }}>
          {needsFeedback.length} awaiting your feedback, {soon.length} in the next 7 days, {done.length} completed
        </span>
      </div>

      {error && <div className="error-banner"><span>{error}</span></div>}
      {loading && <p style={{ color: '#6B7280' }}>Loading...</p>}

      {!loading && (
        <>
          <h3 style={{ margin: '1rem 0 0.5rem' }}>Awaiting your feedback</h3>
          <div className="assessments-grid">
            {needsFeedback.length === 0
              ? <div className="empty-state"><p>Nothing waiting on you.</p></div>
              : needsFeedback.map(i => row(i, (
                <button className="btn btn-primary btn-sm" onClick={() => setFeedbackInterview(i)}>
                  Submit feedback ({daysAgo(endOf(i))})
                </button>
              )))}
          </div>

          <h3 style={{ margin: '1.5rem 0 0.5rem' }}>Coming up</h3>
          <div className="assessments-grid">
            {upcoming.length === 0
              ? <div className="empty-state"><p>No interviews scheduled.</p></div>
              : upcoming.map(i => row(i, i.meetingLink
                ? <a className="btn btn-secondary btn-sm" href={i.meetingLink} target="_blank" rel="noopener noreferrer">Join meeting</a>
                : <span style={{ color: '#6B7280', fontSize: '0.85rem' }}>No meeting link yet</span>))}
          </div>
        </>
      )}

      <InterviewFeedbackDrawer
        interview={feedbackInterview}
        isOpen={Boolean(feedbackInterview)}
        onClose={() => setFeedbackInterview(null)}
        onSubmitted={() => { setFeedbackInterview(null); load(); }}
      />
    </div>
  );
}

export default InterviewQueue;
